// client/src/Components/ModelUploader.jsx
import React, { useState } from 'react';
import axios from 'axios';

const ModelUploader = ({ productId, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
    setError(null);
  };
  
  const validateFiles = () => {
    if (files.length === 0) {
      setError('Please select files to upload');
      return false;
    }
    
    // Check for at least one model file
    const modelFile = files.find(file => 
      file.name.toLowerCase().endsWith('.obj') || 
      file.name.toLowerCase().endsWith('.fbx') ||
      file.name.toLowerCase().endsWith('.glb') ||
      file.name.toLowerCase().endsWith('.gltf')
    );
    
    if (!modelFile) {
      setError('Please include at least one 3D model file (.obj, .fbx, .glb, or .gltf)');
      return false;
    }
    
    // Check file sizes
    const largeFiles = files.filter(file => file.size > 1024 * 1024 * 1024); // 1GB
    if (largeFiles.length > 0) {
      setError(`Some files exceed the 1GB size limit: ${largeFiles.map(f => f.name).join(', ')}`);
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (files.length === 0) {
      setError('Please select files to upload');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('modelFile', files[0]); // Upload the first file
      
      console.log('Uploading file:', files[0].name);
      
      const response = await axios.post(
        `http://localhost:5000/api/upload/upload-model/${productId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );
      
      setSuccess(true);
      setFiles([]);
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error uploading files');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div className="model-uploader">
      <h3>Upload 3D Model</h3>
      
      {success && (
        <div className="upload-success">
          Model uploaded successfully!
        </div>
      )}
      
      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="file-input-container">
          <input
            type="file"
            onChange={handleFileChange}
            multiple
            disabled={uploading}
            accept=".obj,.fbx,.glb,.gltf,.jpg,.jpeg,.png,.bmp,.tga,.tif,.tiff"
          />
          <p className="file-help-text">
            Select one 3D model file (.obj, .fbx, .glb, or .gltf) and any texture files.
            Max file size: 1GB per file.
          </p>
        </div>
        
        {files.length > 0 && (
          <div className="selected-files">
            <h4>Selected files:</h4>
            <ul>
              {files.map((file, index) => {
                const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
                const sizeDisplay = sizeInMB >= 1000 
                  ? `${(sizeInMB / 1024).toFixed(2)} GB` 
                  : `${sizeInMB} MB`;
                
                return (
                  <li key={index} className={file.size > 1024 * 1024 * 1024 ? 'file-too-large' : ''}>
                    {file.name} ({sizeDisplay})
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        
        {uploading && (
          <div className="upload-progress-container">
            <div className="upload-progress">
              <div 
                className="progress-bar" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
              <span>{uploadProgress}%</span>
            </div>
            <p className="upload-status">
              Uploading... This may take a while for large files.
            </p>
          </div>
        )}
        
        <button 
          type="submit" 
          disabled={uploading || files.length === 0}
          className="upload-button"
        >
          {uploading ? 'Uploading...' : 'Upload Model'}
        </button>
      </form>
    </div>
  );
};

export default ModelUploader;